
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import ThemeToggle from "./ThemeToggle";
import useAuth from "../helper/useAuth";

const LOCALE_PREFIXES = ["en"];
// const LOCALE_PREFIXES = ["en", "ru"];
const LANGUAGE_OPTIONS = [
  { code: "hy", labelKey: "language.armenian", flagSrc: "/assets/images/thumbs/hy-flag.png" },
  { code: "en", labelKey: "language.english", flagSrc: "/assets/images/thumbs/en-flag.png" },
  // { code: "ru", labelKey: "language.russian", flagText: "RU" },
];

const HeaderOne = ({
  setOpenGetDemoModal,
  setOpenLoginModal,
  setOpenRegisterModal,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage =
    LANGUAGE_OPTIONS.find((language) => language.code === i18n.language) ||
    LANGUAGE_OPTIONS[0];
  const [active, setActive] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const languageMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const mobileAccountMenuRef = useRef(null);
  const headerRef = useRef(null);
  const drawerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const getLocalizedPath = (lang, path = "") => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    if (lang === "hy") {
      return normalizedPath === "/" ? "/" : normalizedPath;
    }
    return normalizedPath === "/" ? `/${lang}` : `/${lang}${normalizedPath}`;
  };
  const { isAuthenticated, logout } = useAuth();

  useLayoutEffect(() => {
    const header = headerRef.current;
    const row = header.querySelector(".header-inner");
    let disposed = false;
    let resizeFrame;
    const updateLayout = () => {
      if (disposed) return;
      const fits = () => {
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        const visibleChildren = [...row.children].filter(
          (child) => getComputedStyle(child).display !== "none"
        );
        const width = visibleChildren.reduce(
          (total, child) => total + child.getBoundingClientRect().width, 0
        );
        return width + gap * Math.max(0, visibleChildren.length - 1) <= row.clientWidth;
      };
      header.dataset.layout = "full";
      if (window.innerWidth < 1200 || !fits()) {
        header.dataset.layout = window.innerWidth < 600 ? "mobile" : "tablet";
        if (header.dataset.layout === "tablet" && !fits()) {
          header.dataset.layout = "mobile";
        }
      }
      if (header.dataset.layout === "full") setActive(false);
    };
    updateLayout();
    let observedWidth = row.getBoundingClientRect().width;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width === observedWidth) return;
      observedWidth = width;
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(updateLayout);
    });
    observer.observe(row);
    window.addEventListener("resize", updateLayout);
    header.addEventListener("load", updateLayout, true);
    document.fonts.ready.then(updateLayout);
    return () => {
      disposed = true;
      cancelAnimationFrame(resizeFrame);
      observer.disconnect();
      window.removeEventListener("resize", updateLayout);
      header.removeEventListener("load", updateLayout, true);
    };
  }, [i18n.language, isAuthenticated]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout(i18n.language || "en");
      navigate(`/${i18n.language}`, { replace: true });
    } finally {
      setLoggingOut(false);
      setAccountOpen(false);
    }
  };

  const profilePath = getLocalizedPath(i18n.language, "/profile");
  const ticketsPath = getLocalizedPath(i18n.language, "/online-booking/tickets");
  const bookingPath = getLocalizedPath(i18n.language, "/online-booking");

  useEffect(() => {
    setActive(false);
    setAccountOpen(false);
    setLanguageOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!active) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerRef.current.querySelector("button").focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActive(false);
      if (event.key !== "Tab") return;
      const items = [...drawerRef.current.querySelectorAll('a[href], button:not([disabled])')];
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [active]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset < 150) {
        setScroll(false);
      } else {
        setScroll(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!accountOpen && !languageOpen) return;

    const handlePointerDown = (event) => {
      const clickedDesktopMenu = accountMenuRef.current?.contains(event.target);
      const clickedMobileMenu = mobileAccountMenuRef.current?.contains(event.target);
      if (!clickedDesktopMenu && !clickedMobileMenu) {
        setAccountOpen(false);
      }
      if (!languageMenuRef.current?.contains(event.target)) {
        setLanguageOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setAccountOpen(false);
        setLanguageOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [accountOpen, languageOpen]);

  const mobileMenu = () => {
    setAccountOpen(false);
    setActive(!active);
  };

  const handleChangeLanguage = (lang) => {
    setLanguageOpen(false);
    i18n.changeLanguage(lang);

    const pathParts = location.pathname.split("/").filter(Boolean);
    const hasLocalePrefix = LOCALE_PREFIXES.includes(pathParts[0]);
    const routeParts = hasLocalePrefix ? pathParts.slice(1) : pathParts;
    const routePath = routeParts.length ? `/${routeParts.join("/")}` : "/";

    navigate(getLocalizedPath(lang, routePath) + location.search, { replace: true });
  };
  const renderLanguageFlag = (language = currentLanguage) => (
    language.flagSrc ? (
      <img src={language.flagSrc} alt="" className="lng-icon" />
    ) : (
      <span className="lng-icon lng-icon--text" aria-hidden="true">
        {language.flagText || language.code.toUpperCase()}
      </span>
    )
  );
  const renderLanguageOptions = () => (
    <ul>
      {LANGUAGE_OPTIONS.map((language) => (
        <li key={language.code} onClick={() => handleChangeLanguage(language.code)}>
          {t(language.labelKey)}
        </li>
      ))}
    </ul>
  );
  const moveHomeClick = (event, href) => {
    event.preventDefault();
    const homePath = getLocalizedPath(i18n.language, "/");
    if (location.pathname !== homePath) {
      navigate(homePath, { replace: false });
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeMobileMenu = () => {
    setActive(false);
  }

  return (
    <>
      <div className="overlay"></div>
      <div
        className={`side-overlay ${active && "show"}`}
        onClick={closeMobileMenu}
        role="presentation"
      ></div>
      {/* ==================== Header Start Here ==================== */}
      <header ref={headerRef} className={`header header-responsive ${scroll ? "fixed-header" : ""} `}>
        <div className="container container-full">
          <nav className="header-inner flx-between">
            {/* Logo Start */}
            <div className="logo">
              <Link to={getLocalizedPath(i18n.language, "/")} className="link header-mobile-logo">
                <img src="/assets/images/logo/logo-mobile.svg" alt="Logo" />
              </Link>
              <Link to={getLocalizedPath(i18n.language, "/")} className="link white-version" >
                <img src="/assets/images/logo/logo-two.png" alt="Logo" />
              </Link>
              <Link to={getLocalizedPath(i18n.language, "/")} className="link dark-version">
                <img src="/assets/images/logo/white-logo.png" alt="Logo" />
              </Link>
            </div>
            {/* Logo End  */}
            {/* Menu Start  */}
            <div className="header-menu">
              <ul className="nav-menu flx-align">
                <li className="nav-menu__item">
                  <a className="nav-menu__link" href="#about" onClick={(event) => moveHomeClick(event, "#about")}>{t('about')}</a>
                </li>
                <li className="nav-menu__item">
                  <a className="nav-menu__link" href="#solutions" onClick={(event) => moveHomeClick(event, "#solutions")}>{t('solutions')}</a>
                </li>

                <li className="nav-menu__item">
                  {/* has-submenu */}
                  <NavLink to={getLocalizedPath(i18n.language, "/blog")} className="nav-menu__link">
                    {t('blog')}
                  </NavLink>
                </li>
                <li className="nav-menu__item">
                  <NavLink to={getLocalizedPath(i18n.language, "/contact")} className="nav-menu__link">
                    {t('contact')}
                  </NavLink>
                </li>
                {/* <li className="nav-menu__item">
                  <NavLink
                    to={`/${i18n.language}/demo`}
                    className="nav-menu__link d-inline-flex align-items-center gap-2"
                  >
                    <i
                      className="las la-flask"
                      style={{ fontSize: "1.1rem" }}
                      aria-hidden="true"
                    />
                    {t('header.demoEnv')}
                    <span
                      className="badge fw-700 px-2 rounded-pill"
                      style={{
                        backgroundColor: "hsl(263 70% 55%)",
                        color: "#fff",
                        fontSize: 9,
                        letterSpacing: "0.08em",
                      }}
                    >
                      DEMO
                    </span>
                  </NavLink>
                </li> */}

              </ul>
            </div>
            {/* Menu End  */}
            {/* Header Right start */}
            <div className="header-right flx-align">
              <div className="header-mobile-utilities flx-align">
                <ThemeToggle />
                {isAuthenticated ? (
                  <div
                    className="mobile-header-account"
                    ref={mobileAccountMenuRef}
                  >
                    <button
                      type="button"
                      className="mobile-header-account__trigger"
                      onClick={() => {
                        setActive(false);
                        setAccountOpen((value) => !value);
                      }}
                      aria-expanded={accountOpen}
                      aria-haspopup="menu"
                      aria-label={t("profile.headerBtn")}
                    >
                      <i className="las la-user" aria-hidden="true" />

                      <svg
                        className={`mobile-header-account__caret ${accountOpen ? "is-open" : ""
                          }`}
                        viewBox="3.7 6 6.6 3.7"
                        aria-hidden="true"
                      >
                        <path
                          d="M3.8731 9.16093L6.84502 6.18904C6.93045 6.10362 7.06895 6.10362 7.15438 6.18904L10.1262 9.16093C10.264 9.29874 10.1665 9.53436 9.97152 9.53436H4.02778C3.8329 9.53436 3.7353 9.29873 3.8731 9.16093Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                    <div
                      className={`header-account-dropdown mobile-header-account__dropdown ${accountOpen ? "is-open" : ""}`}
                      role="menu"
                    >
                      <NavLink to={profilePath} className="header-account-dropdown__item" role="menuitem" onClick={() => setAccountOpen(false)}>
                        <span className="header-account-design-icon" aria-hidden="true"><img src="/assets/images/icons/account-profile.svg" width="24" height="24" alt="" /></span>
                        <span><strong>{t("profile.headerBtn")}</strong><small>{t("header.accountMenu.profileHint")}</small></span>
                      </NavLink>
                      <NavLink to={ticketsPath} className="header-account-dropdown__item" role="menuitem" onClick={() => setAccountOpen(false)}>
                        <span className="header-account-design-icon" aria-hidden="true"><img src="/assets/images/icons/account-bookings.svg" width="24" height="24" alt="" /></span>
                        <span><strong>{t("header.accountMenu.bookings")}</strong><small>{t("header.accountMenu.bookingsHint")}</small></span>
                      </NavLink>
                      <NavLink to={bookingPath} end className="header-account-dropdown__item" role="menuitem" onClick={() => setAccountOpen(false)}>
                        <i className="las la-calendar-plus" aria-hidden="true" />
                        <span><strong>{t("header.onlineBooking")}</strong><small>{t("header.accountMenu.newBookingHint")}</small></span>
                      </NavLink>
                      <div className="header-account-dropdown__divider" />
                      <button type="button" className="header-account-dropdown__item header-account-dropdown__item--danger" role="menuitem" onClick={handleLogout} disabled={loggingOut}>
                        <i className="las la-sign-out-alt" aria-hidden="true" />
                        <span><strong>{loggingOut ? t("authModal.logout.loading") : t("authModal.logout.btn")}</strong></span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="header-right__inner gap-2 flx-align">
                {isAuthenticated ? (
                  <div className="header-account-menu" ref={accountMenuRef}>
                    <button
                      type="button"
                      className={`header-account-trigger ${accountOpen ? "isOpen" : ""}`}
                      onClick={() => setAccountOpen((value) => !value)}
                      aria-expanded={accountOpen}
                      aria-haspopup="menu"
                    >
                      <span className="header-account-avatar" aria-hidden="true">
                        <img src="/assets/images/icons/user-black.svg" alt="" />
                      </span>
                      {t("profile.headerBtn")}
                      <i
                        className={`las la-caret-${accountOpen ? "up" : "down"} header-account-caret`}
                        aria-hidden="true"
                      />
                    </button>
                    <div
                      className={`header-account-dropdown ${accountOpen ? "is-open" : ""}`}
                      role="menu"
                    >
                      <NavLink
                        to={profilePath}
                        className="header-account-dropdown__item"
                        role="menuitem"
                        onClick={() => setAccountOpen(false)}
                      >
                        <span className="header-account-design-icon" aria-hidden="true">
                          <img src="/assets/images/icons/account-profile.svg" width="24" height="24" alt="" />
                        </span>
                        <span>
                          <strong>{t("profile.headerBtn")}</strong>
                          <small>{t("header.accountMenu.profileHint")}</small>
                        </span>
                      </NavLink>
                      <NavLink
                        to={ticketsPath}
                        className="header-account-dropdown__item"
                        role="menuitem"
                        onClick={() => setAccountOpen(false)}
                      >
                        <span className="header-account-design-icon" aria-hidden="true">
                          <img src="/assets/images/icons/account-bookings.svg" width="24" height="24" alt="" />
                        </span>
                        <span>
                          <strong>{t("header.accountMenu.bookings")}</strong>
                          <small>{t("header.accountMenu.bookingsHint")}</small>
                        </span>
                      </NavLink>

                      <div className="header-account-dropdown__divider" />
                      <button
                        type="button"
                        className="header-account-dropdown__item header-account-dropdown__item--danger"
                        role="menuitem"
                        onClick={handleLogout}
                        disabled={loggingOut}
                      >
                        <i className="las la-sign-out-alt" aria-hidden="true" />
                        <span>
                          <strong>
                            {loggingOut
                              ? t("authModal.logout.loading")
                              : t("authModal.logout.btn")}
                          </strong>
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-main fw-500 header-login-button"
                      onClick={() => setOpenLoginModal(true)}
                    >
                      {t("login")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-main header-register-button"
                      onClick={() => setOpenRegisterModal(true)}
                    >
                      <span className="icon-left icon">
                        <img src="/assets/images/icons/user-black.svg" alt="" />
                      </span>
                      {t("register")}
                    </button>
                  </>
                )}
                <NavLink
                  id="demo-button-header"
                  to={bookingPath}
                  className="btn btn-main header-booking-button"
                >
                  <span className="icon-left icon">
                    <img src="/assets/images/icons/ticket.png" alt="" style={{ width: 17, height: 17 }} />
                  </span>
                  {t('header.onlineBooking')}
                </NavLink>
                <div ref={languageMenuRef} className={`language-select d-flex align-items-center select-has-icon ${languageOpen ? "is-open" : ""}`}>
                  {renderLanguageFlag()}
                  <div className="select py-0 ps-2 border-0 fw-500" tabIndex={0} aria-expanded={languageOpen} aria-label={t(currentLanguage.labelKey)} onClick={(event) => { if (!event.target.closest("ul")) setLanguageOpen((value) => !value); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setLanguageOpen((value) => !value); } }}><span className="header-language-label">{t(currentLanguage.labelKey)}</span>
                    {renderLanguageOptions()}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="toggle-mobileMenu"
                aria-label="Menu"
                aria-expanded={active}
                aria-controls="offcanvas-navigation"
                onClick={mobileMenu}
              >
                <i className="las la-bars" aria-hidden="true" />
              </button>
            </div>
            {/* Header Right End  */}
          </nav>
        </div>
      </header>
      {/* ==================== Header End Here ==================== */}

      <div ref={drawerRef} className={`mobile-menu header-drawer ${active ? "active" : ""}`} role="dialog" aria-modal={active ? "true" : undefined} aria-label="Menu" aria-hidden={!active} inert={active ? undefined : ""}>
        <button type="button" className="header-drawer__close" aria-label="Close menu" onClick={closeMobileMenu}>
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true"><path d="m7 7 16 16M23 7 7 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>
        <nav aria-label="Mobile navigation">
          <ul className="header-drawer__links" id="offcanvas-navigation">
            <li><a href="#about" onClick={(event) => { closeMobileMenu(); moveHomeClick(event, "#about"); }}>{t('about')}</a></li>
            <li><a href="#solutions" onClick={(event) => { closeMobileMenu(); moveHomeClick(event, "#solutions"); }}>{t('solutions')}</a></li>
            <li><NavLink to={getLocalizedPath(i18n.language, "/blog")} onClick={closeMobileMenu}>{t('blog')}</NavLink></li>
            <li><NavLink to={getLocalizedPath(i18n.language, "/contact")} onClick={closeMobileMenu}>{t('contact')}</NavLink></li>
          </ul>
        </nav>
        <div className="header-drawer__actions">
          {!isAuthenticated && (
            <div className="header-drawer__auth">
              <button type="button" onClick={() => { setOpenLoginModal(true); closeMobileMenu(); }}>{t("login")}</button>
              <button type="button" onClick={() => { setOpenRegisterModal(true); closeMobileMenu(); }}><img src="/assets/images/icons/user-black.svg" width="20" height="20" alt="" />{t("register")}</button>
            </div>
          )}
          <NavLink to={bookingPath} onClick={closeMobileMenu} className="btn btn-main header-drawer__booking">
            <img src="/assets/images/icons/ticket.png" width="20" height="20" alt="" />{t('header.onlineBooking')}
          </NavLink>
        </div>
      </div>
      <style>
        {`
          .header-account-menu {
            position: relative;
            flex-shrink: 0;
          }

          .header-login-button,
          .header-register-button,
          .header-booking-button {
            border-radius: 10px;
          }

          .header-login-button {
            height: 38px;
            padding: 10px 16px !important;
            border: 1px solid #111827 !important;
            border-color: #111827 !important;
            color: #111827 !important;
          }

          .header-login-button:hover,
          .header-login-button:focus,
          .header-login-button:focus-visible {
            border-color: #111827 !important;
            background-color: rgba(17, 24, 39, 0.08) !important;
            color: #111827 !important;
          }

          .header-register-button {
            height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 10px 16px !important;
            background: transparent !important;
            background-color: transparent !important;
            border: 1px solid #111827 !important;
            border-color: #111827 !important;
            color: #111827 !important;
          }

          .header-register-button .icon {
            display: inline-flex;
            align-items: center;
            line-height: 1;
          }

          .header-register-button .icon img {
            width: 16px;
            height: 16px;
            display: block;
          }

          .header-booking-button {
            height: 38px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 10px 16px !important;
          }

          .header-booking-button .icon {
            display: inline-flex;
            align-items: center;
            line-height: 1;
          }

          .header-booking-button .icon img {
            width: 16px !important;
            height: 16px !important;
            display: block;
          }

          .header-register-button::before,
          .header-register-button::after {
            display: none !important;
          }

          .header-register-button:hover,
          .header-register-button:focus,
          .header-register-button:focus-visible {
            background-color: rgba(17, 24, 39, 0.08) !important;
            border-color: #111827 !important;
            color: #111827 !important;
          }

          .lng-icon--text {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            background: rgba(0, 209, 178, 0.14);
            color: #00d1b2;
            font-size: 9px;
            font-weight: 800;
            letter-spacing: 0;
          }

          .header-account-trigger {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            height: 38px;
            padding: 9px 16px;
            border: 1px solid;
            border-radius: 12px;
            background: transparent;
            color: #111315;
            font-family: inherit;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
            white-space: nowrap;
            cursor: pointer;
          }

          .header-account-trigger:hover {
  border-color: #00D1B2;
}

.header-account-trigger.isOpen {
  border-color: #00D1B2;
}
          .header-account-trigger:focus-visible {
            outline: 2px solid #00D1B2;
            outline-offset: 3px;
          }

          .header-account-caret {
            font-size: 12px;
          }

          .header-account-avatar {
            width: 24px;
            height: 24px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .header-account-avatar img {
            display: block;
            width: 24px;
            height: 24px;
          }

          html[data-theme="light"] .header-account-trigger {
            color: #FFFFFF;
          }

          html[data-theme="light"] .header-account-avatar img {
            filter: brightness(0) invert(1);
          }

          .header-account-dropdown {
            position: absolute;
            top: calc(100% + 12px);
            right: 0;
            width: min(310px, calc(100vw - 24px));
            padding: 10px;
            border-radius: 18px;
            background: #ffffff;
            border: 1px solid rgba(15, 23, 42, 0.08);
            box-shadow: 0 22px 60px rgba(15, 23, 42, 0.18);
            opacity: 0;
            visibility: hidden;
            transform: translateY(-6px);
            transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
            z-index: 120;
          }

          .header-account-dropdown.is-open {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
          }

          .header-account-dropdown::before {
            content: "";
            position: absolute;
            top: -7px;
            right: 34px;
            width: 14px;
            height: 14px;
            transform: rotate(45deg);
            background: #ffffff;
            border-left: 1px solid rgba(15, 23, 42, 0.08);
            border-top: 1px solid rgba(15, 23, 42, 0.08);
          }

          .header-account-dropdown__item {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 11px 12px;
            border-radius: 12px;
            border: 0;
            background: transparent;
            color: #111827;
            text-decoration: none;
            text-align: left;
            transition: background 0.14s ease, color 0.14s ease;
          }

          .header-account-dropdown__item:hover,
          .header-account-dropdown__item:focus {
            background: rgba(0, 209, 178, 0.1);
            color: #08111f;
          }

          .header-account-dropdown .header-account-dropdown__item[aria-current="page"] {
            background: rgba(0, 209, 178, 0.14);
            color: #00856F;
          }

          html[data-theme="light"] .header-account-dropdown .header-account-dropdown__item[aria-current="page"] {
            color: #00D1B2;
          }

          .header-account-dropdown__item i {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(15, 23, 42, 0.06);
            color: hsl(166 100% 34%);
            font-size: 1.15rem;
            flex-shrink: 0;
          }

          .header-account-design-icon {
            width: 36px;
            height: 36px;
            flex-shrink: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 7px;
            background: #f0f1f9;
          }

          .header-account-design-icon img {
            display: block;
            width: 24px;
            height: 24px;
          }

          html[data-theme="light"] .header-account-design-icon {
            background: rgba(255, 255, 255, 0.08);
          }

          .header-account-dropdown__item strong {
            display: block;
            font-size: 0.92rem;
            line-height: 1.2;
            font-weight: 700;
          }

          .header-account-dropdown__item small {
            display: block;
            margin-top: 2px;
            color: #6b7280;
            font-size: 0.78rem;
            line-height: 1.35;
          }

          .header-account-dropdown__divider {
            height: 1px;
            background: rgba(15, 23, 42, 0.08);
            margin: 8px 4px;
          }

          .header-account-dropdown__item--danger {
            cursor: pointer;
          }

          .header-account-dropdown__item--danger i {
            color: #ef4444;
            background: rgba(239, 68, 68, 0.1);
          }

          .header-account-dropdown__item--danger:hover,
          .header-account-dropdown__item--danger:focus {
            background: rgba(239, 68, 68, 0.08);
            color: #991b1b;
          }

          .mobile-header-account {
            position: relative;
            display: inline-flex;
            flex-shrink: 0;
          }

          .header-mobile-utilities {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }

          .mobile-header-account__trigger {
            width: 148px;
            height: 62px;
            border-radius: 18px;
            border: 2px solid #00d1b2;
            background: #fff;
            color: hsl(var(--heading-color));
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 16px;
            font-size: 1.35rem;
            transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
          }

          .mobile-header-account__caret {
            font-size: 0.8rem;
          }

          .mobile-header-account__trigger:hover,
          .mobile-header-account__trigger:focus,
          .mobile-header-account__trigger[aria-expanded="true"] {
            background: rgba(0, 209, 178, 0.12);
            color: hsl(166 100% 35%);
            box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.12);
          }

          html[data-theme="light"] .mobile-header-account__trigger {
            background: #131517;
            color: #fff;
          }

          .mobile-header-account__dropdown {
            top: calc(100% + 14px);
            right: -42px;
            z-index: 140;
          }

          @media (max-width: 575px) {
            .header-right {
              gap: 10px;
            }

            .mobile-header-account__trigger {
              width: 128px;
              height: 54px;
              border-radius: 16px;
            }

            .mobile-header-account__dropdown {
              position: fixed;
              top: 64px;
              right: 14px;
              width: min(300px, calc(100vw - 28px));
            }

            .mobile-header-account__dropdown::before {
              display: none;
            }
          }

          html[data-theme="light"] .header-account-dropdown {
            background: #131517;
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 0 22px 60px rgba(0, 0, 0, 0.38);
          }

          html[data-theme="light"] .header-login-button,
          html[data-theme="light"] .header-register-button {
            border: 1px solid #ffffff !important;
            border-color: #ffffff !important;
            color: #ffffff !important;
          }

          html[data-theme="light"] .header-login-button:hover,
          html[data-theme="light"] .header-login-button:focus,
          html[data-theme="light"] .header-login-button:focus-visible,
          html[data-theme="light"] .header-register-button:hover,
          html[data-theme="light"] .header-register-button:focus,
          html[data-theme="light"] .header-register-button:focus-visible {
            border-color: #ffffff !important;
            background-color: rgba(255, 255, 255, 0.12) !important;
            color: #ffffff !important;
          }

          html[data-theme="light"] .header-register-button .icon img {
            filter: brightness(0) invert(1);
          }

          html[data-theme="light"] .header-account-dropdown::before {
            background: #131517;
            border-color: rgba(255, 255, 255, 0.1);
          }

          html[data-theme="light"] .header-account-dropdown__item {
            color: #f8fafc;
          }

          html[data-theme="light"] .header-account-dropdown__item:hover,
          html[data-theme="light"] .header-account-dropdown__item:focus {
            background: rgba(0, 209, 178, 0.12);
            color: #ffffff;
          }

          html[data-theme="light"] .header-account-dropdown__item i {
            background: rgba(255, 255, 255, 0.08);
            color: hsl(166 100% 47%);
          }

          html[data-theme="light"] .header-account-dropdown__item small {
            color: rgba(255, 255, 255, 0.68);
          }

          html[data-theme="light"] .header-account-dropdown__divider {
            background: rgba(255, 255, 255, 0.1);
          }

          html[data-theme="light"] .header-account-dropdown__item--danger {
            color: #fecaca;
          }

          html[data-theme="light"] .header-account-dropdown__item--danger i {
            background: rgba(239, 68, 68, 0.13);
            color: #f87171;
          }
        `}
      </style>
    </>
  );
};

export default HeaderOne;
